import React, { Suspense, lazy } from "react";
import { useSelector } from "react-redux";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import LanguageSelector from "../components/LanguageSelector";
import ProtectedRoute from "./ProtectedRoute";
import GoogleCallback from "../features/auth/components/GoogleCallback";
import SignInMobile from "../features/auth/components/SignInMobile";
import SignInEmail from "../features/auth/components/SignInEmail";
import SignUpEmail from "../features/auth/components/SignUpEmail";
import VerifyEmail from "../features/auth/components/VerifyEmail";
import SetPassword from "../features/auth/components/SetPassword";
import AddMobileNumber from "../features/auth/components/AddMobileNumber";
import VerifyMobile from "../features/auth/components/VerifyMobile";
import AddUserName from "../features/auth/components/AddUserName";
import TermsAcceptance from "../features/auth/components/TermsAcceptance";
import SetPickupAddress from "../features/auth/components/SetPickupAddress";
import ForgotPassword from "../features/auth/components/ForgotPassword";
import ResetPassword from "../features/auth/components/ResetPassword";

const Dashboard = lazy(() => import("../features/dashboard/pages/Dashboard"));
const Overview = lazy(() => import("../features/dashboard/pages/Overview"));
const Profile = lazy(() => import("../features/dashboard/pages/Profile"));
const Listing = lazy(() => import("../features/dashboard/pages/Listings/Listings"));
const ListingDetail = lazy(() => import("../features/dashboard/pages/Listings/ListingDetail"));
const BatchDetail = lazy(() => import("../features/dashboard/pages/Batches/BatchDetail"));
const ActiveListing = lazy(() => import("../features/dashboard/pages/ActiveListing"));
const Tasks = lazy(() => import("../features/dashboard/pages/Tasks"));
const Performance = lazy(() => import("../features/dashboard/pages/Performance"));
const Notifications = lazy(() => import("../features/dashboard/pages/Notifications"));
const Support = lazy(() => import("../features/dashboard/pages/Support"));
const PublicPreview = lazy(() => import("../features/dashboard/pages/PublicPreview"));
const StoreEdit = lazy(() => import("../features/dashboard/pages/StoreEdit"));
const Terms = lazy(() => import("../features/dashboard/pages/Terms"));
const Privacy = lazy(() => import("../features/dashboard/pages/Privacy"));
const Takedown = lazy(() => import("../features/dashboard/pages/Takedown"));
const Settings = lazy(() => import("../features/dashboard/pages/Settings"));
const Reviews = lazy(() => import("../features/dashboard/pages/Reviews"));
const RateLimit = lazy(() => import("../features/error/pages/RateLimit"));

function FloatingLanguageSelector() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/dashboard")) return null;

  return (
    <div className="fixed right-3 top-3 z-40">
      <LanguageSelector compact />
    </div>
  );
}

export default function AppRoutes() {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const isLoading = useSelector((state) => state.auth.isLoading);
  return (
    <Router>
      <FloatingLanguageSelector />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route
            path="/"
            element={
              isLoading ? (
                <LoadingSpinner />
              ) : (
                <Navigate
                  to={isAuthenticated ? "/dashboard" : "/auth/sign-in"}
                  replace
                />
              )
            }
          />
          {/* Auth Routes */}
          <Route path="/auth/sign-in" element={<SignInMobile />} />
          <Route path="/auth/sign-in-with-email" element={<SignInEmail />} />
          <Route path="/auth/sign-up-with-email" element={<SignUpEmail />} />
          <Route path="/auth/verify-email" element={<VerifyEmail />} />
          <Route path="/auth/set-password" element={<SetPassword />} />
          <Route path="/auth/add-mobile-number" element={<AddMobileNumber />} />
          <Route path="/auth/verify-mobile-number" element={<VerifyMobile />} />
          <Route path="/auth/add-username" element={<AddUserName />} />
          <Route path="/auth/terms" element={<TermsAcceptance />} />
          <Route path="/auth/set-address" element={<SetPickupAddress />} />

          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/auth/success" element={<GoogleCallback />} />
          {/* Public Legal Pages for unauthenticated users */}
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/takedown" element={<Takedown />} />
          {/* Protected Route */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          >
            {/* Dashboard Routes */}
            <Route index element={<Navigate to="overview" replace />} />{" "}
            {/* 👈 Default main content view when access to Dashboard */}
            <Route path="overview" element={<Overview />} />
            <Route path="listing" element={<Listing />} />
            <Route path="listing/:id" element={<ListingDetail />} />
            <Route path="batch/:id" element={<BatchDetail />} />
            <Route path="active-listing" element={<ActiveListing />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="support" element={<Support />} />
            <Route path="performance" element={<Performance />} />
            <Route path="profile" element={<Profile />} />
            {/* Store preview + edit */}
            <Route path="public-preview" element={<PublicPreview />} />
            <Route path="store" element={<PublicPreview />} />
            <Route path="store/edit" element={<StoreEdit />} />
            {/* Settings */}
            <Route path="settings" element={<Settings />} />
            <Route path="settings/reviews" element={<Reviews />} />
            <Route path="settings/takedown" element={<Takedown />} />
            <Route path="settings/terms" element={<Terms />} />
            <Route path="settings/privacy" element={<Privacy />} />
          </Route>

          <Route path="/error/429" element={<RateLimit />} />

          {/* Fallback Route */}
          <Route path="*" element={<Navigate to="/auth/sign-in" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
}
